import { useMprcTheme } from "components/MprcThemeProvider";
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import * as React from 'react';
import { useState } from "react";

/* <PasteContainer
  onPaste={files => files.length > 0 && props.onFileAccepted(files[0], files[0])}
/> */

// NOTE: `onPaste` should return an array of files that were acceptedFiles
//
// const Image = ({ src }) => <img src={image} alt={`Pasted: ${image}`} />
//
// <PasteContainer
//    onPaste={files => method(files)}
//    onError={err => console.error(err)}
// >
//   {({images}) => images.map((image, i) => <Image src={image} key={i} />)}
// </PasteContainer>

export interface GluejarState {
    images: File[];
}

export interface GluejarProps {
    childrenImages?: (state: GluejarState) => React.ReactNode;
    container?: HTMLElement;
    onPaste: (images: File[]) => void;
    onError: (error: string) => void;
    acceptedFiles?: string[];
}

type BlobLikeFile = File | null;

export const MprcPasteContainer: React.FC<GluejarProps> = (props) => {
    const { theme } = useMprcTheme();

    const [state, setState] = useState<GluejarState>({
        images: []
    });

    const { images } = state;
    const {
        container,
        childrenImages,
        acceptedFiles = ['image/gif', 'image/png', 'image/jpeg', 'image/bmp'],
        onPaste = () => null,
        onError = () => null
    } = props;

    const getContainer = (): HTMLElement | Document => container || window.document;

    const isValidFormat = (fileType: string): boolean => acceptedFiles!.includes(fileType);

    const pasteHandler = (e: Event) => checkPasted(e as ClipboardEvent, pushImage);

    const transformImages = (data: DataTransfer, cb: Function) => {
        // NOTE: This needs to be a for loop, it's a list like object
        for (let i = 0; i < data.items.length; i++) {
            if (isValidFormat(data.items[i].type) !== false) {
                const blob: BlobLikeFile = data.items[i].getAsFile();
                if (blob) {
                    // We shouldn't fire the callback if we can't create `new Blob()`
                    cb(blob);
                }
            } else {
                onError(theme.MprcPasteContainer.transformError);
            }
        }
    };

    const checkPasted = (e: ClipboardEvent, cb: Function) => {
        e.clipboardData && e.clipboardData.items.length > 0
            ? transformImages(e.clipboardData, cb)
            : onError(theme.MprcPasteContainer.pastedError);
    };

    const pushImage = (source: File) =>
        setState(({ images }: GluejarState) => ({ images: [...images, source] }));

    React.useEffect(() => {
        const elm = getContainer();
        elm.addEventListener('paste', pasteHandler);

        return () => {
            const elm = getContainer();
            elm.removeEventListener('paste', pasteHandler);
        };
    }, []);

    useDidUpdateEffect(() => {
        onPaste(state.images);
    }, [state.images]);

    return childrenImages
        ? <>{childrenImages({ images })}</>
        : null;
};
